/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import { cn } from '@bem-react/classname'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { ToastType } from '@/core/services/Toast/types/ToastType'
import countryList from 'react-select-country-list'

import {
  DataGrid,
  GridCellEditStopReasons,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid'

import Box from '@mui/material/Box'

import { Memo } from '@/hoc/Memo'
import useBackendError from '@/hooks/useBackendError'
import { CustomerModel, LicensesCollectionModel } from './models/LicensesModel'
import { RoleUserModel } from '@/core/services/Token/model/TokenModel'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { changeLoading } from '@/core/services/Loading/store/loading.slice'
import {
  getLicencesThunk,
  licensesReassignResellerThunk,
  updateLicenseThunk,
} from './store/licenses.slice'
import { licensesSelector } from './store/licenses.selector'
import {
  connectedReselleridSelector,
  currentUserSelector,
  roleSelector,
  userRoleSelector,
} from '../AccountPage/store/accont.selector'

import { Modal } from '@/components/Modal'
import { Title } from '@/components/Title'
import { Button } from '@/components/Button'
import { CustomerModal } from './components/CustomerModal'

import './styles/LicensesPage.scss'
import { stringify } from 'query-string'
import { CountryDataModel } from '../AccountPage/models/UserModel'
import { ExportExcelButtonSuperadmin } from '@/components/ExportExcelButtonSuperadmin'
import { ExportExcelButtonReseller } from '@/components/ExportExcelButtonReseller'
import {
  DEFAULT_PAGE_SIZE,
  DG_RESELLER_ID,
  FALLBACK_VALUE,
  LICENSE_VERSION_LABELS,
  MAX_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from '@/config/constants'
import { useLicenseAutoReload } from './hooks/useLicenseAutoReload'
import ResellerModal from './components/ResellerModal/ResellerModal'
import { paths } from '@/config'
import Transactions from './components/Transactions/Transactions'

const cnLicenses = cn('Licenses')

export const LicensesPage: FC = Memo(() => {
  const { t, i18n, ready } = useTranslation(['translation', 'countries'])
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [setBackendError] = useBackendError()
  const licenses = useAppSelector(licensesSelector)
  const currentUser = useAppSelector(currentUserSelector)
  const role = useAppSelector(roleSelector)
  const userRole = useAppSelector(userRoleSelector)
  const [countries, setCountries] = useState<CountryDataModel[]>([])
  const isShowTransactions =
    role === RoleUserModel.SuperUser || role === RoleUserModel.Reseller

  const [isOpenCustomerInfo, setOpenCustomerInfo] = useState<boolean>(false)
  const [isReassignModalOpen, setIsReassignModalOpen] = useState<boolean>(false)
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] =
    useState<LicensesCollectionModel | null>(null) // Store selected row
  const [
    customer,
    //setCustomer
  ] = useState<(CustomerModel & { id: string }) | null>(null)
  const [isResellerModalOpen, setIsResellerModalOpen] = useState<boolean>(false)

  const isRenewLicenseAvailable = role !== RoleUserModel.SuperUser
  const connectedResellerId = useAppSelector(connectedReselleridSelector)
  const isShowInvoicedDirectlyColumn =
    userRole === RoleUserModel.SuperUser &&
    connectedResellerId !== DG_RESELLER_ID

  const loadLicenses = useCallback(
    async (isHideLoading = false) => {
      if (!isHideLoading) {
        dispatch(changeLoading(true))
      }
      try {
        await dispatch(
          getLicencesThunk({
            join: 'version,transaction',
            order: 'desc',
            orderBy: 'activationDate',
            pageSize: MAX_PAGE_SIZE,
          }),
        ).unwrap()
      } catch (errors) {
        setBackendError(errors)
      } finally {
        dispatch(changeLoading(false))
      }
    },
    [dispatch],
  )

  useEffect(() => {
    loadLicenses()
  }, [])

  useLicenseAutoReload(licenses, loadLicenses)

  useEffect(() => {
    const list = countryList().getData()
    const upd = list.map(el => {
      return {
        ...el,
        label: t(`countries:${el.value}`),
      }
    })

    if (i18n.language === 'en-GB') setCountries(list)
    else setCountries(upd)
  }, [i18n, ready])

  const renewLicense = useCallback(
    (
      data: LicensesCollectionModel,
    ): // id: string,
    //, versionName: string
    void => {
      const customer = {
        customerName: data.customerName,
        customerSurname: data.customerSurname,
        customerFarm: data.customerFarm,
        customerPhone: data.customerPhone,
        customerAddress: data.customerAddress,
        customerEmail: data.customerEmail,
        countryCode: data.countryCode,
        note: data.note,
      }
      const query = stringify({
        licenseId: data.id,
        ...customer,
        resellerId: data.resellerId,
      })
      navigate(
        `/cabinet/licenses/renew?${query}`,
        // `/cabinet/licenses/renew?licenseId=${id}&versionName=${versionName}`,
      )
    },
    [],
  )

  const addLicense = useCallback((): void => {
    //navigate(`/cabinet/add-license`)
    navigate(paths.CREATE_LICENSE_BY_SERIAL_NUMBER)
  }, [])

  // const customerInfo = useCallback(
  //   (customer: CustomerModel & { id: string }): void => {
  //     setCustomer(customer)
  //     setOpenCustomerInfo(true)
  //   },
  //   [],
  // )

  const handleUpdatedCellValue = async (
    id: string,
    fieldName: string,
    value?: string,
  ) => {
    const data = {
      id,
      [fieldName]: value,
    }

    try {
      const res = await dispatch(updateLicenseThunk(data)).unwrap()
      if (res) {
        dispatch(
          setToast({
            type: ToastType.success,
            message: t('Customer data successfully updated'),
          }),
        )
      }
    } catch (errors) {
      setBackendError(errors)
    }
  }

  const getCountryName = (countryCode: any) => {
    const country = countries.find(item => item.value === countryCode)
    return country ? country.label : ''
  }

  // function renderEditCell(params: GridRenderEditCellParams) {
  //   return <EditInputCell {...params} />
  // }

  // function EditInputCell(props: GridRenderEditCellParams) {
  //   const { error } = props
  //   const errorStyles = { border: '1px solid red' }
  //   return (
  //     <div style={error ? errorStyles : undefined}>
  //       <GridEditInputCell {...props} />
  //     </div>
  //   )
  // }

  const cellRenderLicenseVersion = useCallback(
    ({ row }: GridRenderCellParams<LicensesCollectionModel>) => {
      const licenseVersionId = row.versionId
      const licenseVersionName = row.name

      return (
        <>
          {LICENSE_VERSION_LABELS[licenseVersionId] ||
            licenseVersionName ||
            FALLBACK_VALUE}
        </>
      )
    },
    [],
  )

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('Version'),
      flex: 1,
      hideable: false,
      headerClassName: 'header',
      minWidth: 100,
      renderCell: cellRenderLicenseVersion,
    },
    {
      field: 'serialNumber',
      headerName: t('Serial Number'),
      flex: 1,
      hideable: false,
      headerClassName: 'header',
      minWidth: 200,
    },
    {
      field: 'activationDate',
      headerName: t('Activation Date'),
      hideable: false,
      flex: 1,
      minWidth: 200,
      headerClassName: 'header',
      type: 'date',
      valueFormatter: params => dayjs(params?.value).format('DD/MM/YYYY'),
    },
    {
      field: 'expirationDate',
      headerName: t('Expiration Date'),
      hideable: false,
      flex: 1,
      minWidth: 200,
      headerClassName: 'header',
      type: 'date',
      valueFormatter: params => dayjs(params?.value).format('DD/MM/YYYY'),
    },
    {
      field: 'companyCode',
      headerName: t('Company Code'),
      hideable: false,
      flex: 1,
      minWidth: 200,
      headerClassName: 'header',
      renderCell: ({ value }) => {
        return <>{value ? value : '-'}</>
      },
    },
    {
      field: 'stateId',
      headerName: t('Status'),
      hideable: false,
      flex: 1,
      minWidth: 100,
      headerClassName: 'header',
    },
    {
      field: 'note',
      headerName: t('Note'),
      flex: 1,
      minWidth: 200,
      editable: true,
      headerClassName: 'header',
      cellClassName: 'editableColumn',
      renderCell: ({ value }) => {
        return <>{value ? value : '-'}</>
      },
      // width: 150,
    },
    {
      align: 'center',
      field: 'renew',
      headerName: '',
      flex: 1,
      minWidth: 100,
      disableColumnMenu: true,
      hideSortIcons: true,
      headerClassName: 'header',
      filterable: false,
      sortable: false,
      disableReorder: true,
      disableExport: true,
      renderCell: ({ row }: GridRenderCellParams<LicensesCollectionModel>) => {
        return (
          <>
            {!row.demo && (
              <Button
                small
                onClick={() => renewLicense(row)}
                disabled={!isRenewLicenseAvailable}
              >
                {t('Renew')}
              </Button>
            )}
          </>
        )
      },
    },
  ]

  const columnsReseller: GridColDef[] = [
    {
      field: 'name',
      headerName: t('Version'),
      hideable: false,
      headerClassName: 'header',
      flex: 1,
      minWidth: 200,
      renderCell: cellRenderLicenseVersion,
    },
    {
      field: 'serialNumber',
      headerName: t('Serial Number'),
      hideable: false,
      headerClassName: 'header',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'activationDate',
      headerName: t('Activation Date'),
      hideable: false,
      minWidth: 200,
      headerClassName: 'header',
      type: 'date',
      valueFormatter: params => dayjs(params?.value).format('DD/MM/YYYY'),
      flex: 1,
    },
    {
      field: 'expirationDate',
      headerName: t('Expiration Date'),
      hideable: false,
      minWidth: 200,
      headerClassName: 'header',
      type: 'date',
      valueFormatter: params => dayjs(params?.value).format('DD/MM/YYYY'),
      flex: 1,
    },
    {
      field: 'companyCode',
      headerName: t('Company Code'),
      hideable: false,
      minWidth: 200,
      flex: 1,
      headerClassName: 'header',
      renderCell: ({ value }) => {
        return <>{value ? value : '-'}</>
      },
    },
    {
      field: 'stateId',
      headerName: t('Status'),
      hideable: false,
      minWidth: 150,
      headerClassName: 'header',
      flex: 1,
    },
    {
      field: 'customerName',
      headerName: t('Customer Name'),
      flex: 1,
      editable: true,
      hideable: false,
      headerClassName: 'header',
      cellClassName: 'editableColumn',
      minWidth: 200,

      // preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
      //   const hasError = params.props.value.length < 3
      //   const id = params.id
      //   if (hasError) {
      //     const columnErrors = errors.customerName

      //     if (columnErrors) {
      //       columnErrors.id(id)
      //     } else {
      //       {...errors, customerName}
      //       errors.customerName = new Set(id)
      //     }
      //   }

      //   return { ...params.props, error: hasError }
      // },
      // renderEditCell,
    },
    {
      field: 'customerSurname',
      headerName: t('Customer Surname'),
      editable: true,
      hideable: false,
      headerClassName: 'header',
      cellClassName: 'editableColumn',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'customerEmail',
      headerName: t('Customer Email'),
      editable: true,
      hideable: false,
      headerClassName: 'header',
      cellClassName: 'editableColumn',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'customerPhone',
      headerName: t('Customer Phone'),
      editable: true,
      headerClassName: 'header',
      cellClassName: 'editableColumn',
      renderCell: ({ value }) => {
        return <>{value ? value : '-'}</>
      },
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'customerAddress',
      headerName: t('Customer Address'),
      editable: true,
      headerClassName: 'header',
      cellClassName: 'editableColumn',
      renderCell: ({ value }) => {
        return <>{value ? value : '-'}</>
      },
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'countryCode',
      headerName: t('Customer Country'),
      editable: false,
      headerClassName: 'header',
      valueOptions: countries,
      renderCell: params => {
        const countryName = getCountryName(params.value)
        return <span>{countryName}</span>
      },
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'customerFarm',
      headerName: t('Farm Name'),
      flex: 1,
      hideable: false,
      headerClassName: 'header',
      minWidth: 200,
    },
    {
      field: 'note',
      headerName: t('Note'),
      minWidth: 150,
      flex: 1,
      editable: true,
      headerClassName: 'header',
      cellClassName: 'editableColumn',
      renderCell: ({ value }) => {
        return <>{value ? value : '-'}</>
      },
    },
    ...(role === RoleUserModel.SuperUser
      ? [
          {
            field: 'userEmail',
            headerName: t('User Email'),
            hideable: false,
            headerClassName: 'header',
            flex: 1,
            minWidth: 250,
          },
          {
            field: 'usedDiscountCode',
            headerName: t('Used Discount Code'),
            hideable: false,
            headerClassName: 'header',
            minWidth: 200,
            flex: 1,
          },
          {
            field: 'transactionDate',
            headerName: t('Transaction Date'),
            hideable: false,
            headerClassName: 'header',
            minWidth: 200,
            flex: 1,
            type: 'date',
            valueFormatter: (params: any) =>
              params?.value ? dayjs(params?.value).format('DD/MM/YYYY') : null,
          },
          {
            field: 'resellerId',
            headerName: t('Reseller'),
            hideable: false,
            headerClassName: 'header',
            minWidth: 200,
            flex: 1,
          },
          {
            field: 'disconnect',
            headerName: t('Unlink'),
            flex: 1,
            minWidth: 100,
            disableColumnMenu: true,
            hideSortIcons: true,
            headerClassName: 'header',
            filterable: false,
            sortable: false,
            disableReorder: true,
            disableExport: true,
            renderCell: ({
              row,
            }: GridRenderCellParams<LicensesCollectionModel>) => {
              const isShowDisconnectButton =
                row.name.includes('8') &&
                row.stateId === 'active' &&
                row.resellerId !== 'DG'

              const handleOpenModal = () => {
                setIsReassignModalOpen(true)
                setSelectedRow(row) // Store the row data
              }

              return (
                <>
                  <Button
                    small
                    color='red'
                    onClick={handleOpenModal}
                    isDisabled={!isShowDisconnectButton}
                  >
                    {t('Unlink')}
                  </Button>
                </>
              )
            },
          },
        ]
      : []),

    {
      align: 'center',
      field: 'renew',
      headerName: t('Renew'),
      flex: 1,
      minWidth: 100,
      disableColumnMenu: true,
      hideSortIcons: true,
      headerClassName: 'header',
      filterable: false,
      sortable: false,
      disableReorder: true,
      disableExport: true,
      renderCell: ({ row }: GridRenderCellParams<LicensesCollectionModel>) => {
        return (
          <>
            <Button
              small
              onClick={() => renewLicense(row)}
              isDisabled={row.demo || !isRenewLicenseAvailable}
            >
              {t('Renew')}
            </Button>
          </>
        )
      },
    },
  ]

  // TODO: Refactor columns
  if (role === RoleUserModel.SuperUser) {
    columnsReseller.push({
      align: 'left',
      field: 'selectReseller',
      headerName: t('Select reseller'),
      flex: 1,
      minWidth: 200,
      disableColumnMenu: true,
      hideSortIcons: true,
      headerClassName: 'header',
      filterable: false,
      sortable: false,
      disableReorder: true,
      disableExport: true,
      renderCell: ({ row }: GridRenderCellParams<LicensesCollectionModel>) => {
        const isDisableSelectReseller = row.resellerId === DG_RESELLER_ID

        const handleOpenModal = () => {
          setIsResellerModalOpen(true)
          setSelectedRow(row)
        }

        return (
          <>
            <Button
              small
              color='orange'
              onClick={handleOpenModal}
              isDisabled={isDisableSelectReseller}
            >
              {t('Select reseller')}
            </Button>
          </>
        )
      },
    })
  }

  if (isShowInvoicedDirectlyColumn) {
    const buttonsColumnsQuantity = 3

    columnsReseller.splice(columnsReseller.length - buttonsColumnsQuantity, 0, {
      field: 'createdByDg',
      headerName: t('Invoiced directly'),
      hideable: false,
      minWidth: 200,
      headerClassName: 'header',
      renderCell: ({ value }) => {
        return <>{value ? t('Yes') : t('No')}</>
      },
    })
  }

  const handleUnlink = async () => {
    if (selectedRow) {
      setIsModalLoading(true)
      try {
        await dispatch(licensesReassignResellerThunk(selectedRow.id)).unwrap()
        setIsReassignModalOpen(false)
        setIsModalLoading(false)
        window.location.reload()
      } catch (errors) {
        setBackendError(errors)
        setIsReassignModalOpen(false)
        setIsModalLoading(false)
      }
    }
  }

  const onCancelReassign = () => {
    setIsReassignModalOpen(false)
    setSelectedRow(null) // Clear selected row
  }

  const rows: any = licenses?.collection.map(row => ({
    id: row.id,
    name: row.version?.name,
    serialNumber: row.serialNumber,
    activationDate: row.activationDate,
    expirationDate: row.expirationDate,
    companyCode: row.companyCode,
    stateId: row.stateId,
    note: row.note,
    demo: row.demo,
    resellerId: row.resellerId,
    versionId: row.version?.id,
  }))

  const rowsReseller: any = licenses?.collection.map(row => ({
    id: row.id,
    name: row.version?.name,
    serialNumber: row.serialNumber,
    activationDate: row.activationDate,
    expirationDate: row.expirationDate,
    companyCode: row.companyCode,
    stateId: row.stateId,
    customerName: row.customerName,
    customerSurname: row.customerSurname,
    customerFarm: row.customerFarm,
    countryCode: row.countryCode,
    customerEmail: row.customerEmail,
    customerPhone: row.customerPhone,
    customerAddress: row.customerAddress,
    note: row.note,
    userEmail: row.transaction?.userEmail,
    usedDiscountCode: row.transaction?.usedDiscountCode,
    transactionDate: row.transaction?.transactionDate,
    demo: row.demo,
    resellerId: row.resellerId,
    createdByDg: row.transaction?.createdByDg,
    versionId: row.version?.id,
  }))

  const data = rowsReseller?.map(
    ({ id, ...rest }: { id: number; [key: string]: any }) => rest,
  )

  const excelExportData = data?.map((el: any) => ({
    ...el,
    name: LICENSE_VERSION_LABELS[el.versionId] || el.name,
  }))

  return (
    <>
      <Helmet>
        <title>{t('Licences')} | Registration</title>
      </Helmet>
      <div className={cnLicenses()}>
        <div className={cnLicenses('header')}>
          {licenses && licenses.collection.length > 0 && (
            <Title className={cnLicenses('title')}>{t('My Orders')}</Title>
          )}

          {isShowTransactions && <Transactions />}
        </div>

        {role === RoleUserModel.SuperUser && (
          <ExportExcelButtonSuperadmin data={excelExportData} />
        )}
        {role === RoleUserModel.Reseller && (
          <ExportExcelButtonReseller data={excelExportData} />
        )}
        {licenses && licenses.collection.length > 0 && (
          <Box
            className={cnLicenses('box', {
              largeFooter: role === RoleUserModel.SuperUser,
            })}
            sx={{
              width: '100%',
              borderRight: 'none',
              '& .header': {
                backgroundColor: '#f6f6f6',
              },
              '& .editableColumn': {
                backgroundColor: '#f6f6f6',
                color: 'rgba(0, 0, 0, 0.87)',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: '500',
                color: '#002c53',
                fontSize: '14px',
                fontFamily: "'Be Vietnam Pro', sans-serif",
              },
              '& .MuiDataGrid-cellContent': {
                color: '#4a5056',
                fontSize: '12px',
                fontFamily: "'Be Vietnam Pro', sans-serif",
              },
              '& .MuiDataGrid-root': {
                borderColor: '#FFFFFF',
              },
              '& .MuiDataGrid-withBorderColor': {
                borderColor: '#FFFFFF',
              },
              '& .MuiDataGrid-cell': {
                fontSize: '12px',
                color: '#4a5056',
                fontFamily: "'Be Vietnam Pro', sans-serif",
              },
            }}
          >
            <DataGrid
              editMode='cell'
              rows={
                role === RoleUserModel.Reseller ||
                role === RoleUserModel.SuperUser
                  ? rowsReseller
                  : rows
              }
              columns={
                role === RoleUserModel.Reseller ||
                role === RoleUserModel.SuperUser
                  ? columnsReseller
                  : columns
              }
              // columns={filteredColumns}
              disableRowSelectionOnClick
              disableColumnSelector
              pagination
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE },
                },
              }}
              localeText={{
                MuiTablePagination: {
                  labelRowsPerPage: t('Rows per page'),
                },
              }}
              // components={
              //   role === RoleUserModel.Reseller ||
              //   role === RoleUserModel.SuperUser
              //     ? {
              //         Toolbar: customToolbar,
              //       }
              //     : undefined
              // }
              onCellEditStop={(a, event: any) => {
                if (a.reason === GridCellEditStopReasons.cellFocusOut) {
                  event.defaultMuiPrevented = true
                  return
                }
                handleUpdatedCellValue(
                  a.id as string,
                  a.field as string,
                  event?.target?.value,
                )
              }}
            />
          </Box>
        )}

        {licenses &&
          licenses.collection &&
          licenses.collection.length < 1 &&
          currentUser?.emailConfirmed && (
            <div className={cnLicenses('add')}>
              <Button onClick={addLicense} large>
                {t('Add License')}
              </Button>
            </div>
          )}
      </div>

      <Modal
        isOpen={isReassignModalOpen}
        onClose={onCancelReassign}
        onConfirm={handleUnlink}
        isConfirm
        titlePosition='center'
        buttonPosition='center'
        title={t(
          'Are you sure you want to unlink? This action is permanent, and license management will be transferred to another account.',
        )}
        yesBtnText={t('Unlink')}
        closeBtnText={t('Cancel')}
        isLoading={isModalLoading}
      />

      <Modal
        isOpen={isOpenCustomerInfo}
        title={t('Customer Info')}
        titlePosition='center'
        size='md'
        onClose={() => setOpenCustomerInfo(false)}
        isCloseIcon
      >
        <CustomerModal
          customer={customer}
          onClose={() => {
            setOpenCustomerInfo(false)
            loadLicenses()
          }}
        />
      </Modal>

      <Modal
        isOpen={isResellerModalOpen}
        title={t('Select reseller for license', {
          name:
            LICENSE_VERSION_LABELS[selectedRow?.versionId || ''] ||
            selectedRow?.name ||
            '',
          serialNumber: selectedRow?.serialNumber || '',
        })}
        titlePosition='center'
        size='md'
        onClose={() => setIsResellerModalOpen(false)}
        isCloseIcon
      >
        <ResellerModal
          onCancel={() => {
            setIsResellerModalOpen(false)
          }}
          onChange={() => {
            setIsResellerModalOpen(false)
            loadLicenses()
          }}
          license={selectedRow}
        />
      </Modal>
    </>
  )
})
